/**
 * Industry Template Data
 * Rich, pre-configured templates for 12 different industries
 */

import { IndustryTemplate } from '../types/industryTemplates';
import { SectionIdEnum, SectionId } from '../types/businessPlan';
import { ADDITIONAL_TEMPLATES } from './additionalIndustryTemplates';
import { MORE_TEMPLATES } from './moreIndustryTemplates';
import { FINAL_TEMPLATES } from './finalIndustryTemplates';

export const SAAS_TEMPLATE: IndustryTemplate = {
  id: 'saas',
  name: 'SaaS (Software as a Service)',
  category: 'technology',
  description: 'Subscription-based software businesses with recurring revenue',
  icon: '💻',
  popularity: 95,
  isActive: true,
  
  sectionTemplates: {
    [SectionIdEnum.EXECUTIVE_SUMMARY]: {
      sectionId: SectionIdEnum.EXECUTIVE_SUMMARY,
      templateContent: `# Executive Summary

## Problem Statement
[Describe the specific problem your target customers face]

## Solution
[Explain how your SaaS product solves this problem uniquely]

## Market Opportunity
The global [specific market] is valued at $[X] billion and growing at [Y]% annually. Our target segment represents $[Z] million in addressable market.

## Business Model
Subscription-based SaaS with [monthly/annual] billing. Initial pricing: $[X]/month per [user/seat/account].

## Traction
- [Number] beta users
- $[X] MRR (if applicable)
- [Key milestone achieved]

## Funding Request
Seeking $[X] to achieve [specific milestones] over [timeframe].`,
      guidancePrompts: [
        'What specific problem does your SaaS solve?',
        'Who experiences this problem most acutely?',
        'How is your solution different from existing alternatives?',
        'What traction have you achieved so far?'
      ],
      requiredFields: ['Problem', 'Solution', 'Market Size', 'Business Model', 'Funding Ask'],
      suggestedWordCount: 500
    },
    [SectionIdEnum.PRODUCTS_SERVICES]: {
      sectionId: SectionIdEnum.PRODUCTS_SERVICES,
      templateContent: `# Product & Services

## Core Platform
[Describe your main SaaS platform and its key features]

### Key Features
- **Feature 1**: [Description and value]
- **Feature 2**: [Description and value]
- **Feature 3**: [Description and value]

## Product Roadmap
### Phase 1 (Months 1-6)
- MVP with core features
- [Key feature 1]
- [Key feature 2]

### Phase 2 (Months 7-12)
- [Advanced feature 1]
- [Integration 1]

### Phase 3 (Year 2+)
- [Enterprise features]
- [API platform]

## Technology Stack
- **Frontend**: [Technology]
- **Backend**: [Technology]
- **Database**: [Technology]
- **Hosting**: [Cloud provider]
- **Security**: [Certifications/measures]`,
      guidancePrompts: [
        'What are the 3-5 core features of your MVP?',
        'What makes your technology stack scalable?',
        'How will you handle data security and privacy?',
        'What integrations are most important to your customers?'
      ],
      requiredFields: ['Core Features', 'Technology Stack', 'Roadmap'],
      suggestedWordCount: 600
    }
  },
  
  smartPrompts: {
    industryQuestions: [
      {
        id: 'pricing_model',
        question: 'What is your pricing model?',
        type: 'select',
        options: ['Per user/seat', 'Per account/organization', 'Usage-based', 'Tiered pricing', 'Freemium', 'Custom'],
        required: true,
        appliesTo: [SectionIdEnum.BUSINESS_MODELS, SectionIdEnum.FINANCIALS]
      },
      {
        id: 'target_company_size',
        question: 'What size companies are you targeting?',
        type: 'multiselect',
        options: ['Solo/Freelancers (1-5)', 'Small Business (5-50)', 'Mid-Market (50-500)', 'Enterprise (500+)'],
        required: true,
        appliesTo: [SectionIdEnum.MARKET_RESEARCH, SectionIdEnum.SALES_MARKETING]
      },
      {
        id: 'integration_requirements',
        question: 'What key integrations do your customers need?',
        type: 'textarea',
        placeholder: 'e.g., Salesforce, Slack, Google Workspace, etc.',
        required: false,
        appliesTo: [SectionIdEnum.PRODUCTS_SERVICES]
      },
      {
        id: 'customer_acquisition_channel',
        question: 'Primary customer acquisition channel?',
        type: 'select',
        options: ['Content Marketing/SEO', 'Paid Ads', 'Product-Led Growth', 'Sales Team', 'Partnerships', 'Affiliate'],
        required: true,
        appliesTo: [SectionIdEnum.SALES_MARKETING]
      }
    ],
    editingHints: {
      [SectionIdEnum.FINANCIALS]: [
        'Consider typical SaaS metrics: CAC, LTV, Churn Rate, MRR Growth',
        'Plan for 12-18 months of runway before profitability',
        'Factor in annual vs monthly billing (annual gives better cash flow)'
      ],
      [SectionIdEnum.SALES_MARKETING]: [
        'Focus on CAC payback period (target: <12 months)',
        'Consider freemium or free trial strategy',
        'Plan for content marketing and SEO as primary channels'
      ]
    } as Partial<Record<SectionId, string[]>>
  },
  
  financialDefaults: {
    typicalRevenueStreams: [
      {
        name: 'Subscription Revenue',
        category: 'subscription',
        avgUnitPrice: 99,
        avgUnitsPerMonth: 10,
        growthRate: 25
      }
    ],
    typicalExpenses: [
      {
        name: 'Cloud Hosting',
        category: 'variable',
        avgAmount: 500,
        percentage: 5
      },
      {
        name: 'Software Development',
        category: 'fixed',
        avgAmount: 15000
      },
      {
        name: 'Marketing & Sales',
        category: 'fixed',
        avgAmount: 5000
      },
      {
        name: 'Customer Support',
        category: 'variable',
        avgAmount: 2000,
        percentage: 3
      }
    ],
    benchmarks: {
      grossMargin: { min: 70, avg: 80, max: 90 },
      netMargin: { min: 10, avg: 20, max: 35 },
      revenueGrowth: { min: 50, avg: 100, max: 200 },
      breakEvenMonths: { min: 18, avg: 24, max: 36 }
    },
    typicalStartupCosts: {
      low: 50000,
      medium: 150000,
      high: 500000
    }
  },
  
  keyMetrics: ['MRR', 'ARR', 'Churn Rate', 'CAC', 'LTV', 'LTV/CAC Ratio', 'Net Revenue Retention'],
  
  commonChallenges: [
    {
      challenge: 'High customer acquisition costs',
      solution: 'Implement product-led growth strategy with freemium tier and focus on organic channels like SEO and content marketing',
      resources: ['Product-Led Growth book', 'Y Combinator Startup School videos']
    },
    {
      challenge: 'Managing churn rate',
      solution: 'Focus on customer success, implement proactive outreach, and use data to identify at-risk accounts early',
      resources: ['CustomerSuccessBox', 'ChurnZero platform']
    },
    {
      challenge: 'Scaling technical infrastructure',
      solution: 'Use managed cloud services, implement monitoring early, and plan for horizontal scaling from day one',
      resources: ['AWS Well-Architected Framework', 'Datadog monitoring']
    }
  ]
};

export const ECOMMERCE_TEMPLATE: IndustryTemplate = {
  id: 'ecommerce',
  name: 'E-commerce / Online Retail',
  category: 'retail',
  description: 'Online stores selling physical or digital products',
  icon: '🛍️',
  popularity: 90,
  isActive: true,
  
  sectionTemplates: {
    [SectionIdEnum.EXECUTIVE_SUMMARY]: {
      sectionId: SectionIdEnum.EXECUTIVE_SUMMARY,
      templateContent: `# Executive Summary

## Business Overview
[Your store name] is an e-commerce business specializing in [product category]. We serve [target customer demographic] seeking [key benefit/value proposition].

## Product Line
We offer [X] SKUs across [Y] categories, focusing on [unique selling proposition - e.g., sustainable, handmade, tech-forward, etc.].

## Market Opportunity
The online [product category] market is growing at [X]% annually, with [specific opportunity or gap in market].

## Revenue Model
- Average Order Value: $[X]
- Target Monthly Orders: [X]
- Gross Margin: [X]%

## Competitive Advantage
[What makes your store unique - curation, sourcing, customer experience, etc.]

## Funding Need
Seeking $[X] for inventory, marketing, and platform development.`,
      guidancePrompts: [
        'What products will you sell?',
        'Who is your ideal customer?',
        'What makes your store different from Amazon or other retailers?',
        'How will you source your products?'
      ],
      requiredFields: ['Product Category', 'Target Customer', 'Unique Value Proposition', 'Revenue Model'],
      suggestedWordCount: 450
    },
    [SectionIdEnum.PRODUCTS_SERVICES]: {
      sectionId: SectionIdEnum.PRODUCTS_SERVICES,
      templateContent: `# Products & Services

## Product Categories

### Category 1: [Name]
- **Product Range**: [Description]
- **Price Points**: $[X] - $[Y]
- **Target Margin**: [X]%
- **Suppliers**: [Source/manufacturer info]

### Category 2: [Name]
- **Product Range**: [Description]
- **Price Points**: $[X] - $[Y]
- **Target Margin**: [X]%

## Sourcing Strategy
[Describe how you'll source products - wholesale, manufacturing, dropshipping, etc.]

## Quality Control
[How you ensure product quality and customer satisfaction]

## Fulfillment
- **Warehousing**: [Own warehouse / 3PL / Dropship]
- **Shipping**: [Carriers and delivery timeframes]
- **Returns**: [Return policy and process]`,
      guidancePrompts: [
        'Where will you source your products?',
        'What are your quality standards?',
        'How will you handle shipping and returns?',
        'What is your inventory management strategy?'
      ],
      requiredFields: ['Product Categories', 'Sourcing Strategy', 'Fulfillment Plan'],
      suggestedWordCount: 550
    }
  },
  
  smartPrompts: {
    industryQuestions: [
      {
        id: 'product_type',
        question: 'What type of products will you sell?',
        type: 'select',
        options: ['Physical goods', 'Digital products', 'Both physical and digital', 'Dropshipping'],
        required: true,
        appliesTo: [SectionIdEnum.PRODUCTS_SERVICES, SectionIdEnum.OPERATIONS]
      },
      {
        id: 'fulfillment_model',
        question: 'How will you fulfill orders?',
        type: 'select',
        options: ['Own warehouse', '3PL (Third-party logistics)', 'Dropshipping', 'Hybrid'],
        required: true,
        appliesTo: [SectionIdEnum.OPERATIONS, SectionIdEnum.FINANCIALS]
      },
      {
        id: 'platform',
        question: 'Which e-commerce platform will you use?',
        type: 'select',
        options: ['Shopify', 'WooCommerce', 'BigCommerce', 'Custom built', 'Amazon FBA', 'Other'],
        required: true,
        appliesTo: [SectionIdEnum.PRODUCTS_SERVICES, SectionIdEnum.FINANCIALS]
      }
    ],
    editingHints: {
      [SectionIdEnum.FINANCIALS]: [
        'Plan for 30-40% gross margins on physical products',
        'Budget 15-25% of revenue for marketing',
        'Factor in returns (typically 5-10% for e-commerce)'
      ],
      [SectionIdEnum.OPERATIONS]: [
        'Consider seasonal inventory needs',
        'Plan for peak season fulfillment capacity',
        'Build relationships with backup suppliers'
      ]
    } as Partial<Record<SectionId, string[]>>
  },
  
  financialDefaults: {
    typicalRevenueStreams: [
      {
        name: 'Product Sales',
        category: 'product',
        avgUnitPrice: 50,
        avgUnitsPerMonth: 200,
        growthRate: 20
      }
    ],
    typicalExpenses: [
      {
        name: 'Cost of Goods Sold (COGS)',
        category: 'variable',
        avgAmount: 0,
        percentage: 60
      },
      {
        name: 'Fulfillment & Shipping',
        category: 'variable',
        avgAmount: 0,
        percentage: 8
      },
      {
        name: 'Marketing & Advertising',
        category: 'variable',
        avgAmount: 2000,
        percentage: 20
      },
      {
        name: 'E-commerce Platform Fee',
        category: 'variable',
        avgAmount: 0,
        percentage: 3
      },
      {
        name: 'Payment Processing',
        category: 'variable',
        avgAmount: 0,
        percentage: 2.9
      }
    ],
    benchmarks: {
      grossMargin: { min: 30, avg: 45, max: 65 },
      netMargin: { min: 5, avg: 15, max: 25 },
      revenueGrowth: { min: 30, avg: 60, max: 150 },
      breakEvenMonths: { min: 12, avg: 18, max: 24 }
    },
    typicalStartupCosts: {
      low: 10000,
      medium: 50000,
      high: 200000
    }
  },
  
  keyMetrics: ['GMV', 'AOV', 'Conversion Rate', 'Cart Abandonment', 'CAC', 'ROAS', 'Return Rate'],
  
  commonChallenges: [
    {
      challenge: 'High customer acquisition costs',
      solution: 'Focus on retention marketing, email campaigns, and organic social media. Build a loyalty program.',
      resources: ['Klaviyo email marketing', 'Facebook Ads Library']
    },
    {
      challenge: 'Inventory management',
      solution: 'Use inventory management software, forecast demand accurately, and maintain good supplier relationships',
      resources: ['Inventory Planner', 'Stocky by Shopify']
    },
    {
      challenge: 'Competition from Amazon',
      solution: 'Differentiate through curation, customer service, niche focus, or unique products not available on Amazon',
      resources: ['DTC community forums', 'Indie Hackers']
    }
  ]
};

// Export all templates (50+ total)
export const ALL_INDUSTRY_TEMPLATES: IndustryTemplate[] = [
  SAAS_TEMPLATE,
  ECOMMERCE_TEMPLATE,
  ...ADDITIONAL_TEMPLATES,
  ...MORE_TEMPLATES,
  ...FINAL_TEMPLATES
];

// Helper function to get template by ID
export function getTemplateById(id: string): IndustryTemplate | undefined {
  return ALL_INDUSTRY_TEMPLATES.find(t => t.id === id);
}

// Helper function to get templates by category
export function getTemplatesByCategory(category: string): IndustryTemplate[] {
  return ALL_INDUSTRY_TEMPLATES.filter(t => t.category === category && t.isActive);
}

// Helper function to get most popular templates
export function getPopularTemplates(limit: number = 6): IndustryTemplate[] {
  return [...ALL_INDUSTRY_TEMPLATES]
    .filter(t => t.isActive)
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, limit);
}
